import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Brush,
  AreaChart,
  Area,
  LabelList,
  Legend,
} from 'recharts';
import { AverageScoreChartTooltip } from '../AverageScoreChartTooltip/AverageScoreChartTooltip';
import { SurveyWithinTimeFrame } from '../../api/queries';
import { DateUtils } from '../../utils/DateUtils';
import { chartAxisProps, chartGridProps, formatChartValue, yAxisTicks } from '../BarChart/chartStyle';

type LineChartProps = {
  disabled?: boolean;
  data: SurveyWithinTimeFrame[];
  /** Kompakt: verdien over hvert punkt, uten fyll, forklaring og zoom */
  variant?: 'default' | 'compact';
  height?: number;
  /** Farge på hvert punkt ut fra verdien, i stedet for aksentfargen */
  colorForScore?: (score: number) => string;
};

type DotProps = { cx?: number; cy?: number; payload?: SurveyWithinTimeFrame; index?: number };

export const LineChart = ({
  data,
  disabled = false,
  variant = 'default',
  height,
  colorForScore,
}: LineChartProps) => {
  const { t } = useTranslation();
  const isCompact = variant === 'compact';

  const dataWithFormattedDate = data.map((item) => ({
    ...item,
    shortDate: DateUtils.getShortDateFromDate(item.date),
  }));

  const color = disabled ? 'var(--ds-color-neutral-text-subtle)' : 'var(--ds-color-accent-base-default)';

  return (
    <ResponsiveContainer width="100%" height={height ?? (isCompact ? 260 : 450)}>
      <AreaChart
        accessibilityLayer
        data={dataWithFormattedDate}
        margin={{ top: isCompact ? 28 : 10, right: isCompact ? 16 : 30, left: isCompact ? -24 : 0, bottom: 0 }}
      >
        <defs>
          <linearGradient id="averageScore" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid {...chartGridProps} />
        <XAxis dataKey="shortDate" dy={8} height={isCompact ? 36 : 60} padding={{ left: 24, right: 24 }} {...chartAxisProps} />
        <YAxis domain={[0, 5]} ticks={yAxisTicks} dx={-4} {...chartAxisProps} />
        <Tooltip
          aria-describedby="customTooltip"
          cursor={{ stroke: 'var(--ds-color-neutral-border-default)' }}
          content={(props) => (
            <AverageScoreChartTooltip props={props} active={props.active} />
          )}
        />
        {!isCompact && <Legend verticalAlign="top" height={36} />}
        <Area
          name={t('chart.averageScore')}
          type="linear"
          dataKey="averageScore"
          stroke={color}
          strokeWidth={2.5}
          fill={isCompact ? 'transparent' : 'url(#averageScore)'}
          dot={
            colorForScore
              ? ({ cx, cy, payload, index }: DotProps) => (
                  <circle
                    key={index}
                    cx={cx}
                    cy={cy}
                    r={5}
                    strokeWidth={3}
                    stroke={payload ? colorForScore(payload.averageScore) : color}
                    fill="var(--ds-color-neutral-background-default)"
                  />
                )
              : {
                  strokeWidth: 3,
                  stroke: 'var(--ds-color-neutral-background-default)',
                  fill: color,
                  r: 6,
                }
          }
          activeDot={{
            strokeWidth: 3,
            stroke: 'var(--ds-color-neutral-background-default)',
            fill: 'var(--ds-color-accent-base-hover)',
            r: 7,
          }}
        >
          {isCompact && (
            <LabelList
              dataKey="averageScore"
              position="top"
              offset={14}
              formatter={formatChartValue}
              fill="var(--ds-color-neutral-text-default)"
              fontSize={14}
              fontWeight={500}
            />
          )}
        </Area>
        {!isCompact && (
          <Brush
            alwaysShowText
            dataKey={'shortDate'}
            dy={8}
            fill="var(--ds-color-accent-surface-tinted)"
            stroke="var(--ds-color-accent-base-default)"
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
};
